import { useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { ChevronDown, Download, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PremiumBadge } from '~/components/common/premium-badge';
import { GoogleLogo } from '~/components/logos/GoogleLogo';
import { MetaLogo } from '~/components/logos/MetaLogo';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { CatalogAlertBanner } from '~/features/catalog-operations/components/catalog-alert-banner';
import { CatalogBatchTable } from '~/features/catalog-operations/components/catalog-batch-table';
import { CatalogOperationsLayout } from '~/features/catalog-operations/components/catalog-operations-layout';
import { CatalogStatCard } from '~/features/catalog-operations/components/catalog-stat-card';
import { NewScheduleMenu } from '~/features/catalog-operations/components/schedules/new-schedule-menu';
import { SchedulesSection } from '~/features/catalog-operations/components/schedules/schedules-section';
import { TrialCatalogEnrichedBanner } from '~/features/catalog-operations/components/trial-catalog-enriched-banner';
import { useTrialBatches } from '~/features/catalog-operations/hooks/use-catalog-batches';
import {
  useCatalogStats,
  useCurationStats,
} from '~/features/catalog-operations/hooks/use-catalog-stats';
import type {
  AttentionCategory,
  CatalogStatCardProps,
  CatalogStats,
  CurationStatsBucket,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { useScheduledGoogleStore } from '~/features/content-enrichment/store/scheduled-google.store';
import { useScheduledMetaStore } from '~/features/content-enrichment/store/scheduled-meta.store';
import { useOnboardingStatus } from '~/features/onboarding/hooks/use-onboarding-status';
import { useSubsidiaryProducts } from '~/features/platform/hooks/use-subsidiary-products';
import { useTrialProductLimit } from '~/features/trial/hooks/use-trial-product-limit';
import { useAuthStore } from '~/store/auth.store';
import { PLATFORM_PRODUCT_CODES } from '~/store/trial.store';
import { cn } from '~/utils/cn';

const NEEDS_ATTENTION_LABEL = 'Needs Attention';

/** Formats a 0–1 ratio as a signed percentage with one decimal place. */
function formatPercentDelta(ratio: number): string {
  const pct = Math.abs(ratio) * 100;
  return `${pct.toFixed(1)}%`;
}

/** Renders milliseconds as a compact hour string ("2.4h") for the latency card. */
function formatLatency(ms: number): string {
  const hours = ms / (1000 * 60 * 60);
  return `${hours.toFixed(1)}h`;
}

/** Connector codes surfaced in the alert-banner breakdown, mapped to their
 *  display-label translation keys. Order here drives display order. */
const CONNECTOR_LABEL_KEYS: readonly (readonly [string, string])[] = [
  ['GOOGLE_MERCHANT', 'curationAlert.googleMerchant'],
  ['META_COMMERCE', 'curationAlert.metaCommerce'],
  ['SHOPIFY', 'curationAlert.shopify'],
];

/** Builds the alert-banner's per-connector breakdown from `/curation/stats`'
 *  `perSource` map. A connector is only surfaced when its own
 *  `needsAttention` count is greater than zero. */
function buildAttentionCategories(
  perSource: Record<string, CurationStatsBucket>,
  t: TFunction<'catalog-operations'>,
): AttentionCategory[] {
  return CONNECTOR_LABEL_KEYS.filter(([code]) => (perSource[code]?.needsAttention ?? 0) > 0).map(
    ([code, labelKey]) => ({
      count: perSource[code]?.needsAttention ?? 0,
      description: t(labelKey),
    }),
  );
}

/** Builds the four stat-card prop objects from the mapped stats payload.
 *  `curationNeedsAttention` comes from /curation/stats and is the authoritative
 *  product count — kept separate because /jobs/summary returns a different
 *  (batch-level) value for the same field. */
function buildStatCards(
  stats: CatalogStats,
  curationNeedsAttention: number,
): CatalogStatCardProps[] {
  const successRatePct = Math.round(stats.successRate * 100);
  const deliveredDelta = stats.totalProductsDeliveredDelta;
  const latencyDelta = stats.avgProcessingLatencyDelta;
  return [
    {
      label: 'Batches this week',
      value: stats.batchesThisWeek.toLocaleString(),
      // Spread so trend is omitted entirely (not set to undefined) when there are no batches.
      // exactOptionalPropertyTypes forbids explicit `undefined` on optional properties.
      ...(stats.batchesThisWeek > 0 && {
        trend: {
          label: `${String(successRatePct)}% Success rate`,
          direction: 'none' as const,
          positive: successRatePct >= 90,
        },
      }),
    },
    {
      label: 'Total Products Delivered',
      value: stats.totalProductsDelivered.toLocaleString(),
      trend: {
        // Show a dash when the backend has no last-week comparison available.
        label:
          deliveredDelta !== null
            ? `${formatPercentDelta(deliveredDelta)} vs last week`
            : '— vs last week',
        direction: deliveredDelta !== null ? (deliveredDelta >= 0 ? 'up' : 'down') : 'none',
        positive: deliveredDelta !== null ? deliveredDelta >= 0 : true,
      },
    },
    {
      label: 'Avg Processing Latency',
      value: formatLatency(stats.avgProcessingLatencyMs),
      trend: {
        label: latencyDelta < 0 ? 'Improving' : latencyDelta > 0 ? 'Slowing' : 'Stable',
        direction: latencyDelta > 0 ? 'up' : latencyDelta < 0 ? 'down' : 'none',
        // Negative delta on latency is good (faster).
        positive: latencyDelta <= 0,
      },
    },
    {
      label: NEEDS_ATTENTION_LABEL,
      value: curationNeedsAttention.toLocaleString(),
      // Only render a trend when severity buckets are available — avoids a
      // misleading "0 categories" label on tenants where /jobs/summary hasn't
      // populated the critical/warning breakdown yet.
      ...(stats.needsAttentionCategories.length > 0 && {
        trend: {
          label: stats.needsAttentionCategories
            .map((c) => `${c.count.toLocaleString()} ${c.description}`)
            .join(', '),
          direction: 'none' as const,
          positive: curationNeedsAttention === 0,
        },
      }),
    },
  ];
}

/** `schedulesFocus` — set by a `?tab=schedules[&scheduleId=]` deep link: the Schedules section
 *  opens and, given an id, narrows to and highlights that schedule. */
export function CatalogOperationsPage({
  schedulesFocus,
}: {
  schedulesFocus?: { scheduleId?: string | undefined } | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const { t: tc } = useTranslation('catalog-export');
  const navigate = useNavigate();
  // Gates the single-shared-batch restriction: it only applies under the 20-product cap.
  const { isMaxTwenty } = useTrialProductLimit();
  const { data: stats, isLoading, isError } = useCatalogStats();
  const resetMetaStore = useScheduledMetaStore((s) => s.reset);
  const resetGoogleStore = useScheduledGoogleStore((s) => s.reset);
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId);
  const { data: enrolledProducts } = useSubsidiaryProducts(subsidiaryId);
  const googleContract =
    enrolledProducts?.find((p) => p.productCode === PLATFORM_PRODUCT_CODES.google)?.contractType ??
    null;
  const metaContract =
    enrolledProducts?.find((p) => p.productCode === PLATFORM_PRODUCT_CODES.meta)?.contractType ??
    null;
  const isAnyTrial = googleContract === 'TRIAL_POC' || metaContract === 'TRIAL_POC';
  // Trial batches never get a real delivery step — AWAITING_APPROVAL is their
  // terminal "completed" state (see `isTrialCompleted` in batch-detail-header.tsx
  // for the equivalent single-batch check). The probe is source=TRIAL and
  // `archived: 'ALL'` so an archived trial batch still counts (see
  // `useTrialBatches`) — it no longer piggybacks on the table's default fetch,
  // which hides archived rows.
  const { items: trialBatches } = useTrialBatches();
  const hasCompletedTrialBatch =
    isAnyTrial && trialBatches.some((b) => b.status === 'completed' || b.status === 'delivered');
  // The single-shared-batch restriction only exists under the trial-max-20 cap,
  // so it's gated on `isMaxTwenty`: once ANY trial batch exists — in any state —
  // creating another trial batch is blocked. With the flag off (500-product
  // trials) the restriction doesn't apply. A LIVE platform is never affected;
  // only TRIAL_POC platforms are gated.
  const hasAnyTrialBatch = trialBatches.length > 0;
  const disableGoogleCreate = isMaxTwenty && googleContract === 'TRIAL_POC' && hasAnyTrialBatch;
  const disableMetaCreate = isMaxTwenty && metaContract === 'TRIAL_POC' && hasAnyTrialBatch;

  // Per-platform readiness: connection must be ACTIVE and catalog import COMPLETE before
  // allowing the user to create a new batch. Otherwise route back to onboarding.
  const { data: onboardingStatus } = useOnboardingStatus();
  const googleChannel = onboardingStatus?.channels.find(
    (c) => c.connectorCode === 'GOOGLE_MERCHANT',
  );
  const isGoogleReady =
    googleChannel?.connectionStatus === 'ACTIVE' && googleChannel.importStatus === 'COMPLETE';
  const metaChannel = onboardingStatus?.channels.find((c) => c.connectorCode === 'META_COMMERCE');
  const isMetaReady =
    metaChannel?.connectionStatus === 'ACTIVE' && metaChannel.importStatus === 'COMPLETE';
  // Banner visibility + count is driven by /curation/stats — independent
  // from /jobs/summary so a stats failure here just hides the banner
  // instead of breaking the page header.
  const { data: curationStats } = useCurationStats();
  const showAlertBanner = (curationStats?.needsAttention ?? 0) > 0;
  const attentionCategories = curationStats
    ? buildAttentionCategories(curationStats.perSource, t)
    : [];

  const cards = stats ? buildStatCards(stats, curationStats?.needsAttention ?? 0) : [];

  // Dropdown trigger button — wrapped in a tooltip only in trial mode, since
  // production batches don't have the enrichment-only caveat.
  const createBatchButton = (
    <DropdownMenuTrigger asChild>
      <Button
        data-testid="catalog-ops-create-batch-button"
        variant="default"
        className="cursor-pointer gap-2"
      >
        <Plus className="h-4 w-4" />
        Create New Batch
        <ChevronDown className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
  );
  const createBatchTrigger = isAnyTrial ? (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{createBatchButton}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-80 text-center">
        {t('listing.createBatchTooltip')}
      </TooltipContent>
    </Tooltip>
  ) : (
    createBatchButton
  );

  // Platform items are "soft-disabled" (styling + guarded handlers) rather than
  // via Radix `disabled`, which sets pointer-events-none and would kill both the
  // not-allowed cursor and the explanatory tooltip on hover.
  const googleMenuItem = (
    <DropdownMenuItem
      data-testid="catalog-ops-create-batch-google-item"
      className={cn(
        'gap-3 px-3 py-2',
        disableGoogleCreate ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
      onSelect={(e) => {
        // Keep the menu open and do nothing once the trial batch limit is hit.
        if (disableGoogleCreate) e.preventDefault();
      }}
      onClick={() => {
        if (disableGoogleCreate) return;
        if (googleContract === 'TRIAL_POC') {
          void navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: '/trial/$platform/create' as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            params: { platform: 'google' } as any,
          });
        } else if (googleContract === 'LIVE') {
          if (isGoogleReady) {
            resetGoogleStore();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            void navigate({ to: '/enrichments/google/product-selection' as any });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            void navigate({ to: '/onboarding/integration/google' as any });
          }
        } else {
          void navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: '/product-not-enrolled' as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            search: { product: 'google' } as any,
          });
        }
      }}
    >
      <span className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        <GoogleLogo className="h-5 w-5" />
      </span>
      Google Ads
    </DropdownMenuItem>
  );

  const metaMenuItem = (
    <DropdownMenuItem
      data-testid="catalog-ops-create-batch-meta-item"
      className={cn(
        'gap-3 px-3 py-2',
        disableMetaCreate ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
      onSelect={(e) => {
        if (disableMetaCreate) e.preventDefault();
      }}
      onClick={() => {
        if (disableMetaCreate) return;
        if (metaContract === 'TRIAL_POC') {
          void navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: '/trial/$platform/create' as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            params: { platform: 'meta' } as any,
          });
        } else if (metaContract === 'LIVE') {
          if (isMetaReady) {
            resetMetaStore();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            void navigate({ to: '/enrichments/meta/product-selection' as any });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            void navigate({ to: '/onboarding/integration/meta' as any });
          }
        } else {
          void navigate({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to: '/product-not-enrolled' as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
            search: { product: 'meta' } as any,
          });
        }
      }}
    >
      <span className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
        <MetaLogo className="h-5 w-5" />
      </span>
      Meta Ads
    </DropdownMenuItem>
  );

  return (
    <CatalogOperationsLayout testId="catalog-ops-page">
      <div data-testid="catalog-ops-header" className="flex items-center justify-between">
        <h1 data-testid="catalog-ops-title" className="text-2xl font-bold tracking-tight">
          Catalog Operations
        </h1>
        <div className="flex items-center gap-2.5">
          {/* Full-catalog export: always offered, like a batch's own export. */}
          <Button
            data-testid="catalog-ops-export-catalog-button"
            variant="outline"
            className="cursor-pointer gap-2"
            onClick={() => {
              void navigate({ to: '/exports/catalog' });
            }}
          >
            <Download className="h-4 w-4" />
            {tc('entryButton')}
          </Button>
          {/* Schedules are set up in the batch wizard, so the channel is picked the same way. */}
          <NewScheduleMenu
            google={{ contract: googleContract, ready: isGoogleReady }}
            meta={{ contract: metaContract, ready: isMetaReady }}
          />
          <DropdownMenu>
            {createBatchTrigger}
            <DropdownMenuContent
              data-testid="catalog-ops-create-batch-menu"
              align="end"
              className="min-w-44"
            >
              {disableGoogleCreate ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>{googleMenuItem}</TooltipTrigger>
                  <TooltipContent side="left" className="max-w-72 text-center">
                    {t('listing.trialBatchLimitTooltip')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                googleMenuItem
              )}
              {disableMetaCreate ? (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>{metaMenuItem}</TooltipTrigger>
                  <TooltipContent side="left" className="max-w-72 text-center">
                    {t('listing.trialBatchLimitTooltip')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                metaMenuItem
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isAnyTrial && (
        <div
          data-testid="catalog-ops-trial-banner"
          className="bg-background text-stage-text mt-4 flex items-center gap-2 rounded-md px-4 py-2.5 text-sm shadow-sm"
        >
          <PremiumBadge size={16} className="shrink-0" />
          <span>{t('listing.trialBanner')}</span>
        </div>
      )}
      {hasCompletedTrialBatch && (
        <div className="mt-5">
          <TrialCatalogEnrichedBanner />
        </div>
      )}
      <div className="mt-6 space-y-4">
        {isLoading ? (
          <Skeleton data-testid="catalog-ops-summary-loading" className="h-16 w-full rounded-xl" />
        ) : isError || !stats ? (
          <div
            data-testid="catalog-ops-summary-error"
            className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700"
          >
            Failed to load catalog summary.
          </div>
        ) : showAlertBanner && curationStats ? (
          <CatalogAlertBanner
            totalCount={curationStats.needsAttention}
            categories={attentionCategories}
            onViewAll={() => {
              void navigate({ to: '/catalog-operations/products-need-attention' });
            }}
          />
        ) : null}

        <div data-testid="catalog-ops-stat-cards" className="flex gap-4">
          {isLoading || !stats
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  data-testid="catalog-ops-stat-card-loading"
                  className="h-24 flex-1 rounded-xl"
                />
              ))
            : cards.map((metric) => (
                <CatalogStatCard
                  key={metric.label}
                  {...metric}
                  isPremium={isAnyTrial}
                  testId={`catalog-ops-stat-card-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))}
        </div>

        {/* Collapsed by default, so it costs one row above the batch table. */}
        <SchedulesSection focus={schedulesFocus} />

        <CatalogBatchTable isTrial={isAnyTrial} />
      </div>
    </CatalogOperationsLayout>
  );
}
